import { APP_CONTEXTS } from "app/model"
import { ACTIONS } from "app/store/actions"
import { IFormInventory } from "form/inventory/model"
import { IAppForm, IForm, IFormDataLoadStatus, IFormState, IGeoTargetsForm } from "form/model"
import { getGeoTargetsChanges } from "form/tools/geo-targets"
import { getGeoTargetsCount } from "form/tools/geo-targets-list"
import { Action, Reducer } from "redux"
import {
  IFormActionClearForm,
  IFormActionCloseSummary,
  IFormActionContext,
  IFormActionRefreshInventory,
  IFormActionSaveError,
  IFormActionSet,
  IFormActionSetActiveSummaryTab,
  IFormActionSetActiveTab,
  IFormActionSetAllOperators,
  IFormActionSetAllSspDeals,
  IFormActionSetAuctions,
  IFormActionSetAudience,
  IFormActionSetAudienceFilter,
  IFormActionSetCampaignSspDeals,
  IFormActionSetCreatives,
  IFormActionSetDspDealFormats,
  IFormActionSetEnvironments,
  IFormActionSetEnvironmentsExpandedIds,
  IFormActionSetEnvironmentsFilter,
  IFormActionSetEnvironmentsForecast,
  IFormActionSetEnvironmentsIsTreeView,
  IFormActionSetGeoTargets,
  IFormActionSetGeoTargetsFileImport,
  IFormActionSetGeoTargetsForm,
  IFormActionSetGeoTargetsSelection,
  IFormActionSetHasError,
  IFormActionSetIsLoaded,
  IFormActionSetIsLoading,
  IFormActionSetIsSavingCampaign,
  IFormActionSetMapInventoryLegendData,
  IFormActionSetMapIsRefreshing,
  IFormActionSetMapVisibility,
  IFormActionSetMediaCreativeForecast,
  IFormActionSetNoIntersectionError,
  IFormActionSetOperatorFilter,
  IFormActionSetOperators,
  IFormActionSetOperatorsForecast,
  IFormActionSetRefreshFormikForm,
  IFormActionSetRefreshInventory,
  IFormActionSetRelations,
  IFormActionSetSsps,
  IFormActionStopSaving,
  IFormActionSummarySet,
  IFormSetCreativeFilterAction,
  IFormSetErrorAction
} from "./actions"
import { getDefaultAppFormState, getDefaultFormState, getFormData } from "./state"

export const formReducer: Reducer<IFormState, Action> = (state: IFormState | undefined, action: Action): IFormState => {
  if (!state) {
    state = getDefaultFormState()
  }
  switch (action.type) {
    case ACTIONS.FORM_SET_ACTIVE_TAB: {
      const { context, activeTab } = action as IFormActionSetActiveTab
      return setFormAndGetNewState(state, {
        ...getFormFromContext(state, context),
        activeTab
      })
    }

    case ACTIONS.FORM_SET_ACTIVE_SUMMARY_TAB: {
      const { context, activeSummaryTab } = action as IFormActionSetActiveSummaryTab
      return setFormAndGetNewState(state, {
        ...getFormFromContext(state, context),
        activeSummaryTab
      })
    }

    case ACTIONS.FORM_SET_IS_SAVING_CAMPAIGN: {
      const { context, isSaving } = action as IFormActionSetIsSavingCampaign
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        context: {
          ...form.context,
          isSavingCampaign: isSaving
        }
      })
    }

    case ACTIONS.FORM_START_SAVING_TEMP_CAMPAIGN: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        context: {
          ...form.context,
          isSavingTempCampaign: true,
          saveError: null
        }
      })
    }

    case ACTIONS.FORM_STOP_SAVING_TEMP_CAMPAIGN: {
      const { context, configurationChanges, lastSavedData } = action as IFormActionStopSaving
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        changes: {
          ...form.changes,
          configuration: configurationChanges
        },
        context: {
          ...form.context,
          isSavingTempCampaign: false,
          saveError: null,
          lastSavedData: lastSavedData ? lastSavedData : form.context.lastSavedData
        }
      })
    }

    case ACTIONS.FORM_SAVE_ERROR: {
      const { context, saveError } = action as IFormActionSaveError
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        context: {
          ...form.context,
          isSavingTempCampaign: false,
          saveError
        }
      })
    }

    case ACTIONS.FORM_SET: {
      const { form, changes, lastSavedData, tempCampaignId, tempDspDealId } = action as IFormActionSet
      const newForm: IForm = {
        ...form,
        tempDspDealId: typeof tempDspDealId === "number" ? tempDspDealId : form.tempDspDealId,
        changes: {
          ...form.changes,
          ...changes
        },
        context: {
          ...form.context,
          isSavingTempCampaign: false,
          saveError: null,
          lastSavedData: lastSavedData ? lastSavedData : form.context.lastSavedData
        }
      }
      const newState = setFormAndGetNewState(state, newForm)
      const inventory = getInventoryFromContext(newState, form.context.appContext)
      return setInventoryAndGetNewState(newState, {
        ...inventory,
        tempCampaignId: typeof tempCampaignId == "number" ? tempCampaignId : inventory.tempCampaignId,
        totalGeoTargets: getGeoTargetsCount(newForm)
      })
    }

    case ACTIONS.FORM_SET_RELATIONS: {
      const { context, relations } = action as IFormActionSetRelations
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        context: {
          ...form.context,
          relations: {
            ...form.context.relations,
            ...relations
          }
        }
      })
    }

    case ACTIONS.FORM_SET_GEO_TARGETS: {
      const { context, geoTargets } = action as IFormActionSetGeoTargets
      const form = getFormFromContext(state, context)
      const newForm = getNewFormWithUpdatedLastSavedData({
        ...form,
        geoTargets: {
          ...form.geoTargets,
          ...geoTargets
        }
      })
      newForm.context.lastSavedData = getFormData(newForm)
      newForm.changes.geoTargets = getGeoTargetsChanges(newForm)
      const newState = setFormAndGetNewState(state, newForm)
      return setInventoryAndGetNewState(newState, {
        ...getInventoryFromContext(newState, form.context.appContext),
        totalGeoTargets: getGeoTargetsCount(newForm)
      })
    }

    case ACTIONS.FORM_SET_SSPS: {
      const { context, sspAssociations, screensChanges } = action as IFormActionSetSsps
      const form = getFormFromContext(state, context)
      const newForm = getNewFormWithUpdatedLastSavedData({
        ...form,
        changes: {
          ...form.changes,
          screens: screensChanges
        },
        context: {
          ...form.context,
          relations: {
            ...form.context.relations,
            ssps: sspAssociations,
            sspsError: false
          },
          screens: {
            ...form.context.screens,
            isValid: sspAssociations.length > 0,
            errors: {
              ...form.context.screens.errors,
              sspIds: sspAssociations.length === 0 ? "length === 0" : undefined
            }
          }
        },
        screens: {
          ...form.screens,
          sspIds: sspAssociations.map(s => s.ssp_id),
          isSynchronizingSsp: false,
          isLoadingSsp: false,
          hasSspSynchronizeError: false
        }
      })
      return setFormAndGetNewState(state, newForm)
    }

    case ACTIONS.FORM_SET_ENVIRONMENTS: {
      const { context, environmentsAssociations, screensChanges } = action as IFormActionSetEnvironments
      const form = getFormFromContext(state, context)
      const newForm = getNewFormWithUpdatedLastSavedData({
        ...form,
        changes: {
          ...form.changes,
          screens: screensChanges
        },
        context: {
          ...form.context,
          relations: {
            ...form.context.relations,
            environments: environmentsAssociations,
            environmentsError: false
          }
        },
        screens: {
          ...form.screens,
          environmentsIds: environmentsAssociations.map(e => e.venue_type_id),
          isSynchronizingEnvironments: false,
          hasEnvironmentsSynchronizeError: false
        }
      })
      return setFormAndGetNewState(state, newForm)
    }

    case ACTIONS.FORM_START_SYNCH_SSPS: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          isSynchronizingSsp: true
        }
      })
    }

    case ACTIONS.FORM_STOP_SYNCH_SSPS: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          isSynchronizingSsp: false
        }
      })
    }

    case ACTIONS.FORM_START_SYNCH_ENVIRONMENTS: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          isSynchronizingEnvironments: true
        }
      })
    }

    case ACTIONS.FORM_STOP_SYNCH_ENVIRONMENTS: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          isSynchronizingEnvironments: false
        }
      })
    }

    case ACTIONS.FORM_SET_ENVIRONMENTS_FILTER: {
      const {
        context,
        params: { name, selectFilter }
      } = action as IFormActionSetEnvironmentsFilter
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          environmentFilter: typeof name === "string" ? name : form.screens.environmentFilter,
          environmentSelectFilter:
            typeof selectFilter === "string" ? selectFilter : form.screens.environmentSelectFilter
        }
      })
    }

    case ACTIONS.FORM_SET_ENVIRONMENTS_EXPANDED_IDS: {
      const { context, expandedIds } = action as IFormActionSetEnvironmentsExpandedIds
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          environmentExpandedIds: expandedIds
        }
      })
    }

    case ACTIONS.FORM_SET_ENVIRONMENTS_IS_TREE_VIEW: {
      const { isTreeView } = action as IFormActionSetEnvironmentsIsTreeView
      return {
        ...state,
        isEnvironmentTreeView: isTreeView
      }
    }

    case ACTIONS.FORM_SET_ENVIRONMENTS_FORECAST: {
      const { context, forecast } = action as IFormActionSetEnvironmentsForecast
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        context: {
          ...form.context,
          environment: {
            ...form.context.environment,
            currentForecast: forecast
          }
        },
        screens: {
          ...form.screens,
          isLoadingEnvironmentsForecast: false
        }
      })
    }

    case ACTIONS.FORM_START_LOAD_ENVIRONMENTS_FORECAST: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          isLoadingEnvironmentsForecast: true
        }
      })
    }

    case ACTIONS.FORM_STOP_LOAD_ENVIRONMENTS_FORECAST: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          isLoadingEnvironmentsForecast: false
        }
      })
    }

    case ACTIONS.FORM_START_SYNCH_SSPS: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          isSynchronizingSsp: true
        }
      })
    }

    case ACTIONS.FORM_STOP_SYNCH_SSPS: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          isSynchronizingSsp: false
        }
      })
    }

    case ACTIONS.FORM_SET_OPERATORS: {
      const { context, operators, screensChanges } = action as IFormActionSetOperators
      const form = getFormFromContext(state, context)
      const newForm = getNewFormWithUpdatedLastSavedData({
        ...form,
        changes: {
          ...form.changes,
          screens: screensChanges
        },
        context: {
          ...form.context,
          relations: {
            ...form.context.relations,
            operators,
            operatorsError: false
          }
        },
        screens: {
          ...form.screens,
          operators: operators.slice(),
          isSynchronizingOperator: false,
          hasOperatorSynchronizeError: false
        }
      })
      return setFormAndGetNewState(state, newForm)
    }

    case ACTIONS.FORM_START_SYNCH_OPERATORS: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          isSynchronizingOperator: true
        }
      })
    }

    case ACTIONS.FORM_STOP_SYNCH_OPERATORS: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          isSynchronizingOperator: false
        }
      })
    }

    case ACTIONS.FORM_SET_OPERATORS_FORECAST: {
      const { context, forecast } = action as IFormActionSetOperatorsForecast
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        context: {
          ...form.context,
          operator: {
            ...form.context.operator,
            currentForecast: forecast
          }
        },
        screens: {
          ...form.screens,
          isLoadingOperatorsForecast: false
        }
      })
    }

    case ACTIONS.FORM_START_LOAD_OPERATORS_FORECAST: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          isLoadingOperatorsForecast: true
        }
      })
    }

    case ACTIONS.FORM_STOP_LOAD_OPERATORS_FORECAST: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          isLoadingOperatorsForecast: false
        }
      })
    }

    case ACTIONS.FORM_SET_AUDIENCE_FILTER: {
      const {
        context,
        params: { name, selectFilter }
      } = action as IFormActionSetAudienceFilter
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        audience: {
          ...form.audience,
          filter: typeof name === "string" ? name : form.audience.filter,
          selectFilter: typeof selectFilter === "string" ? selectFilter : form.audience.selectFilter
        }
      })
    }

    case ACTIONS.FORM_START_SYNCH_AUDIENCE: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        audience: {
          ...form.audience,
          isSynchronizing: true
        }
      })
    }

    case ACTIONS.FORM_STOP_SYNCH_AUDIENCE: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        audience: {
          ...form.audience,
          isSynchronizing: false
        }
      })
    }

    case ACTIONS.FORM_SET_AUDIENCE: {
      const { context, audienceAssociations, audienceChanges } = action as IFormActionSetAudience
      const form = getFormFromContext(state, context)
      const newForm = getNewFormWithUpdatedLastSavedData({
        ...form,
        changes: {
          ...form.changes,
          audience: audienceChanges
        },
        context: {
          ...form.context,
          relations: {
            ...form.context.relations,
            audiences: audienceAssociations,
            audiencesError: false
          }
        },
        audience: {
          ...form.audience,
          selectedIds: audienceAssociations.map(a => a.external_segment_id),
          isSynchronizing: false,
          hasSynchronizeError: false
        }
      })
      return setFormAndGetNewState(state, newForm)
    }

    case ACTIONS.FORM_START_REFRESHING_INVENTORY: {
      const { context } = action as IFormActionRefreshInventory
      return setInventoryAndGetNewState(state, {
        ...getInventoryFromContext(state, context),
        isRefreshing: true,
        hasRefreshError: false
      })
    }

    case ACTIONS.FORM_HAS_REFRESH_INVENTORY_ERROR: {
      const { context } = action as IFormActionRefreshInventory
      return setInventoryAndGetNewState(state, {
        ...getInventoryFromContext(state, context),
        isRefreshing: false,
        hasRefreshError: true
      })
    }

    case ACTIONS.FORM_REFRESH_INVENTORY: {
      const { context, result, name } = action as IFormActionSetRefreshInventory
      const inventory = getInventoryFromContext(state, context)
      return setInventoryAndGetNewState(state, {
        ...inventory,
        campaignName: typeof name === "string" ? name : inventory.campaignName,
        isRefreshing: false,
        hasRefreshError: false,
        lastUpdate: new Date(),
        totalImpressions: result.total_impressions,
        totalScreens: result.total_screens,
        totalSpend: result.total_spend,
        cpmForecastTotalImpressions: result.cpmForecastTotalImpressions,
        cpmForecastTotalScreens: result.cpmForecastTotalScreens,
        cpmForecastTotalSpend: result.cpmForecastTotalSpend,
        cpmForecastGroups: result.cpmForecastGroups
      })
    }

    case ACTIONS.FORM_SET_MAP_IS_REFRESHING: {
      const { context, isRefreshing } = action as IFormActionSetMapIsRefreshing
      return setInventoryAndGetNewState(state, {
        ...getInventoryFromContext(state, context),
        isMapRefreshing: isRefreshing
      })
    }

    case ACTIONS.FORM_SET_MAP_LEGEND_DATA: {
      const { context, data } = action as IFormActionSetMapInventoryLegendData
      return setInventoryAndGetNewState(state, {
        ...getInventoryFromContext(state, context),
        ...data
      })
    }

    case ACTIONS.FORM_START_LOADING: {
      const { context, isLoading } = action as IFormActionSetIsLoading
      return setCampaignLoadAndGetNewState(state, context, { isLoading, error: "" })
    }

    case ACTIONS.FORM_SET_ERROR: {
      const { context, error } = action as IFormSetErrorAction
      return setCampaignLoadAndGetNewState(state, context, { isLoading: false, error })
    }

    case ACTIONS.FORM_SUMMARY_SET: {
      const { form, inventory, geoTargetsForm } = action as IFormActionSummarySet
      if (form.context.isForecast) {
        return {
          ...state,
          forecastEditForm: {
            ...state.forecastEditForm,
            isLoading: false,
            error: "",
            form,
            inventory,
            geoTargetsForm
          }
        }
      } else if (form.context.isDspDeal) {
        return {
          ...state,
          dspDealEditForm: {
            ...state.dspDealEditForm,
            isLoading: false,
            error: "",
            form,
            inventory,
            geoTargetsForm
          }
        }
      } else {
        return {
          ...state,
          campaignEditForm: {
            ...state.campaignEditForm,
            isLoading: false,
            error: "",
            form,
            inventory,
            geoTargetsForm
          }
        }
      }
    }

    case ACTIONS.FORM_SUMMARY_CLOSE: {
      const { isForecast, isDspDeal } = action as IFormActionCloseSummary
      return {
        ...state,
        campaignEditForm:
          !isForecast && !isDspDeal ? getDefaultAppFormState(APP_CONTEXTS.CAMPAIGN_EDIT) : state.campaignEditForm,
        forecastEditForm: isForecast ? getDefaultAppFormState(APP_CONTEXTS.FORECAST_EDIT) : state.forecastEditForm,
        dspDealEditForm: isDspDeal ? getDefaultAppFormState(APP_CONTEXTS.DSP_DEAL_EDIT) : state.dspDealEditForm
      }
    }

    case ACTIONS.FORM_SET_GEO_TARGETS_FORM: {
      const { form, context } = action as IFormActionSetGeoTargetsForm
      return setGeoTargetsFormAndGetNewState(state, context, {
        ...getGeoTargetsFormFromContext(state, context),
        ...form
      })
    }

    case ACTIONS.FORM_SET_NO_INTERSECTION_ERROR: {
      const { context, hasNoIntersectionError } = action as IFormActionSetNoIntersectionError
      const newState = setGeoTargetsFormAndGetNewState(state, context, {
        ...getGeoTargetsFormFromContext(state, context),
        noIntersectionError: hasNoIntersectionError
      })
      const form = getFormFromContext(newState, context)
      return setFormAndGetNewState(newState, {
        ...form,
        context: {
          ...form.context,
          geoTargets: {
            isValid: !hasNoIntersectionError,
            errors: hasNoIntersectionError
              ? {
                  coordinates: "Coordinates and shapes doesn't intersect"
                }
              : {}
          }
        }
      })
    }

    case ACTIONS.FORM_SET_GEO_TARGETS_SELECTION: {
      const { selection, context } = action as IFormActionSetGeoTargetsSelection
      const newGeoTargetsForm = getGeoTargetsFormFromContext(state, context)
      return setGeoTargetsFormAndGetNewState(state, context, {
        ...newGeoTargetsForm,
        selection: {
          ...newGeoTargetsForm.selection,
          ...selection
        }
      })
    }

    case ACTIONS.FORM_SET_MAP_VISIBILITY: {
      const { isVisible, context, viewType } = action as IFormActionSetMapVisibility
      const inventory = getInventoryFromContext(state, context)
      return setInventoryAndGetNewState(state, {
        ...inventory,
        isMapVisible: isVisible,
        mapViewType: viewType ? viewType : inventory.mapViewType
      })
    }

    case ACTIONS.FORM_SET_MAP_IS_LOADING: {
      const { isLoading, context } = action as IFormActionSetIsLoading
      const inventoryState = getInventoryFromContext(state, context)
      return setInventoryAndGetNewState(state, {
        ...inventoryState,
        isMapLoading: isLoading,
        isMapLoaded: isLoading ? false : inventoryState.isMapLoaded
      })
    }

    case ACTIONS.FORM_SET_MAP_IS_LOADED: {
      const { isLoaded, context } = action as IFormActionSetIsLoaded
      return setInventoryAndGetNewState(state, {
        ...getInventoryFromContext(state, context),
        isMapLoading: false,
        isMapLoaded: isLoaded
      })
    }

    case ACTIONS.USER_LOGOUT: {
      return getDefaultFormState()
    }

    case ACTIONS.FORM_CLEAR_APP_FORM: {
      const { context, refreshFormikForm } = action as IFormActionClearForm
      const appForm = getDefaultAppFormState(context)
      if (refreshFormikForm) {
        appForm.form.context.refreshFormikForm = true
      }
      return setAppFormAndGetNewState(state, context, appForm)
    }

    case ACTIONS.FORM_REFRESH_FORMIK_FORM: {
      const { context, refreshFormikForm } = action as IFormActionSetRefreshFormikForm
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        context: {
          ...form.context,
          refreshFormikForm
        }
      })
    }

    case ACTIONS.FORM_SET_AUCTIONS: {
      const { context, auctionAssociations, hasError } = action as IFormActionSetAuctions
      const form = getFormFromContext(state, context)
      const newForm = getNewFormWithUpdatedLastSavedData({
        ...form,
        context: {
          ...form.context,
          relations: {
            ...form.context.relations,
            auctions: auctionAssociations,
            auctionsError: false
          }
        },
        configuration: {
          ...form.configuration,
          budget: {
            ...form.configuration.budget,
            auctions: auctionAssociations.map(a => a.auction_type_id)
          }
        }
      })
      return setFormAndGetNewState(state, newForm)
    }

    case ACTIONS.FORM_SET_GEO_TARGETS_FILE_IMPORT: {
      const { context, fileImport } = action as IFormActionSetGeoTargetsFileImport
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        geoTargets: {
          ...form.geoTargets,
          fileImport: {
            ...form.geoTargets.fileImport,
            ...fileImport
          }
        }
      })
    }

    case ACTIONS.FORM_SET_CREATIVES: {
      const { context, creatives, creativeChange } = action as IFormActionSetCreatives
      const form = getFormFromContext(state, context)
      const newForm = {
        ...form,
        creatives: {
          ...form.creatives,
          ...creatives
        }
      }
      if (creativeChange) {
        newForm.changes = {
          ...newForm.changes,
          creative: [creativeChange]
        }
      }
      return setFormAndGetNewState(state, newForm)
    }

    case ACTIONS.FORM_SET_ALL_OPERATORS: {
      const { context, allOperators } = action as IFormActionSetAllOperators
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          allOperators,
          isLoadingAllOperators: false,
          hasLoadingAllOperatorsError: false
        }
      })
    }

    case ACTIONS.FORM_SET_HAS_LOADING_ALL_OPERATORS_ERROR: {
      const { context, hasError } = action as IFormActionSetHasError
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          allOperators: hasError ? [] : form.screens.allOperators,
          isLoadingAllOperators: hasError ? false : form.screens.hasLoadingAllOperatorsError,
          hasLoadingAllOperatorsError: hasError
        }
      })
    }

    case ACTIONS.FORM_SET_IS_LOADING_ALL_OPERATORS: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          allOperators: [],
          isLoadingAllOperators: true,
          hasLoadingAllOperatorsError: false
        }
      })
    }

    case ACTIONS.FORM_SET_OPERATOR_FILTER: {
      const {
        context,
        params: { name, selectFilter }
      } = action as IFormActionSetOperatorFilter
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          operatorFilter: typeof name === "string" ? name : form.screens.operatorFilter,
          operatorSelectFilter: typeof selectFilter === "string" ? selectFilter : form.screens.operatorSelectFilter
        }
      })
    }

    case ACTIONS.FORM_SET_MEDIA_CREATIVE_FORECAST: {
      const { context, forecast } = action as IFormActionSetMediaCreativeForecast
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        context: {
          ...form.context,
          mediaCreative: {
            ...form.context.mediaCreative,
            currentForecast: forecast
          }
        },
        creatives: {
          ...form.creatives,
          isLoadingForecast: false,
          hasLoadingForecastError: false
        }
      })
    }

    case ACTIONS.FORM_START_LOAD_MEDIA_CREATIVE_FORECAST: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        creatives: {
          ...form.creatives,
          isLoadingForecast: true,
          hasLoadingForecastError: false
        }
      })
    }

    case ACTIONS.FORM_STOP_LOAD_MEDIA_CREATIVE_FORECAST: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        creatives: {
          ...form.creatives,
          isLoadingForecast: false,
          hasLoadingForecastError: true
        }
      })
    }

    case ACTIONS.FORM_START_SYNCH_MEDIA_CREATIVE_FORECAST: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        creatives: {
          ...form.creatives,
          isSynchronizing: true,
          hasSynchronizeError: false
        }
      })
    }

    case ACTIONS.FORM_STOP_SYNCH_MEDIA_CREATIVE_FORECAST: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        creatives: {
          ...form.creatives,
          isSynchronizing: false,
          hasSynchronizeError: false
        }
      })
    }

    case ACTIONS.FORM_SET_HAS_SYNCH_MEDIA_CREATIVE_FORECAST_ERROR: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        creatives: {
          ...form.creatives,
          isSynchronizing: false,
          hasSynchronizeError: true
        }
      })
    }

    case ACTIONS.FORM_SET_CREATIVE_FILTER: {
      const { context, filter } = action as IFormSetCreativeFilterAction
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        creatives: {
          ...form.creatives,
          filter
        }
      })
    }

    case ACTIONS.FORM_SET_ALL_SSP_DEALS: {
      const { context, allSspDeals } = action as IFormActionSetAllSspDeals
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          allSspDeals,
          isLoadingAllSspDeals: false,
          hasLoadingAllSspDealsError: false
        }
      })
    }

    case ACTIONS.FORM_SET_HAS_LOADING_ALL_SSP_DEALS_ERROR: {
      const { context, hasError } = action as IFormActionSetHasError
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          allSspDeals: hasError ? [] : form.screens.allSspDeals,
          isLoadingAllSspDeals: hasError ? false : form.screens.hasLoadingAllSspDealsError,
          hasLoadingAllOperatorsError: hasError
        }
      })
    }

    case ACTIONS.FORM_SET_IS_LOADING_ALL_SSP_DEALS: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          allSspDeals: [],
          isLoadingAllSspDeals: true,
          hasLoadingAllSspDealsError: false
        }
      })
    }

    case ACTIONS.FORM_SET_CAMPAIGN_SSP_DEALS: {
      const { context, campaignSspDeals, screensChanges } = action as IFormActionSetCampaignSspDeals
      const form = getFormFromContext(state, context)
      const newForm = getNewFormWithUpdatedLastSavedData({
        ...form,
        changes: {
          ...form.changes,
          screens: screensChanges
        },
        context: {
          ...form.context,
          relations: {
            ...form.context.relations,
            campaignSspDeals,
            sspDealsError: false
          }
        },
        screens: {
          ...form.screens,
          campaignSspDeals: campaignSspDeals.slice(),
          isSynchronizingSspDeal: false,
          hasSspDealSynchronizeError: false
        }
      })
      return setFormAndGetNewState(state, newForm)
    }

    case ACTIONS.FORM_START_SYNCH_SSP_DEALS: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          isSynchronizingSspDeal: true
        }
      })
    }

    case ACTIONS.FORM_STOP_SYNCH_SSP_DEALS: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        screens: {
          ...form.screens,
          isSynchronizingSspDeal: false
        }
      })
    }

    case ACTIONS.FORM_START_SYNCH_FORMATS: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        configuration: {
          ...form.configuration,
          dspDeal: {
            ...form.configuration.dspDeal,
            isSynchronizingFormat: true
          }
        }
      })
    }

    case ACTIONS.FORM_STOP_SYNCH_FORMATS: {
      const { context } = action as IFormActionContext
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        configuration: {
          ...form.configuration,
          dspDeal: {
            ...form.configuration.dspDeal,
            isSynchronizingFormat: false
          }
        }
      })
    }

    case ACTIONS.FORM_SET_DSP_DEALS_FORMATS: {
      const { context, serverSideFormats, changes } = action as IFormActionSetDspDealFormats
      const form = getFormFromContext(state, context)
      return setFormAndGetNewState(state, {
        ...form,
        configuration: {
          ...form.configuration,
          dspDeal: {
            ...form.configuration.dspDeal,
            formatIds: serverSideFormats.map(f => f.format_id),
            isSynchronizingFormat: false
          }
        },
        context: {
          ...form.context,
          relations: {
            ...form.context.relations,
            dspDealFormats: serverSideFormats.slice()
          }
        },
        changes: {
          ...form.changes,
          configuration: changes
        }
      })
    }

    default:
      return state
  }
}

function setAppFormAndGetNewState(state: IFormState, context: APP_CONTEXTS, appForm: IAppForm): IFormState {
  switch (context) {
    case APP_CONTEXTS.CAMPAIGN_CREATE: {
      return {
        ...state,
        campaignCreateForm: appForm
      }
    }
    case APP_CONTEXTS.CAMPAIGN_EDIT: {
      return {
        ...state,
        campaignEditForm: appForm
      }
    }
    case APP_CONTEXTS.FORECAST_CREATE: {
      return {
        ...state,
        forecastCreateForm: appForm
      }
    }
    case APP_CONTEXTS.FORECAST_EDIT: {
      return {
        ...state,
        forecastEditForm: appForm
      }
    }
    case APP_CONTEXTS.DSP_DEAL_CREATE: {
      return {
        ...state,
        dspDealCreateForm: appForm
      }
    }
    case APP_CONTEXTS.DSP_DEAL_EDIT: {
      return {
        ...state,
        dspDealEditForm: appForm
      }
    }
  }
}

function setCampaignLoadAndGetNewState(
  state: IFormState,
  context: APP_CONTEXTS,
  load: Partial<IFormDataLoadStatus>
): IFormState {
  switch (context) {
    case APP_CONTEXTS.CAMPAIGN_CREATE: {
      return {
        ...state,
        campaignCreateForm: {
          ...state.campaignCreateForm,
          ...load
        }
      }
    }
    case APP_CONTEXTS.CAMPAIGN_EDIT: {
      return {
        ...state,
        campaignEditForm: {
          ...state.campaignEditForm,
          ...load
        }
      }
    }
    case APP_CONTEXTS.FORECAST_CREATE: {
      return {
        ...state,
        forecastCreateForm: {
          ...state.forecastCreateForm,
          ...load
        }
      }
    }
    case APP_CONTEXTS.FORECAST_EDIT: {
      return {
        ...state,
        forecastEditForm: {
          ...state.forecastEditForm,
          ...load
        }
      }
    }
    case APP_CONTEXTS.DSP_DEAL_CREATE: {
      return {
        ...state,
        dspDealCreateForm: {
          ...state.dspDealCreateForm,
          ...load
        }
      }
    }
    case APP_CONTEXTS.DSP_DEAL_EDIT: {
      return {
        ...state,
        dspDealEditForm: {
          ...state.dspDealEditForm,
          ...load
        }
      }
    }
  }
}

function getInventoryFromContext(state: IFormState, context: APP_CONTEXTS): IFormInventory {
  switch (context) {
    case APP_CONTEXTS.CAMPAIGN_CREATE: {
      return state.campaignCreateForm.inventory
    }
    case APP_CONTEXTS.CAMPAIGN_EDIT: {
      return state.campaignEditForm.inventory
    }
    case APP_CONTEXTS.FORECAST_CREATE: {
      return state.forecastCreateForm.inventory
    }
    case APP_CONTEXTS.FORECAST_EDIT: {
      return state.forecastEditForm.inventory
    }
    case APP_CONTEXTS.DSP_DEAL_CREATE: {
      return state.dspDealCreateForm.inventory
    }
    case APP_CONTEXTS.DSP_DEAL_EDIT: {
      return state.dspDealEditForm.inventory
    }
  }
}

function setInventoryAndGetNewState(state: IFormState, inventory: IFormInventory): IFormState {
  switch (inventory.contextType) {
    case APP_CONTEXTS.CAMPAIGN_CREATE: {
      return {
        ...state,
        campaignCreateForm: {
          ...state.campaignCreateForm,
          inventory: {
            ...state.campaignCreateForm.inventory,
            ...inventory
          }
        }
      }
    }
    case APP_CONTEXTS.CAMPAIGN_EDIT: {
      return {
        ...state,
        campaignEditForm: {
          ...state.campaignEditForm,
          inventory: {
            ...state.campaignEditForm.inventory,
            ...inventory
          }
        }
      }
    }
    case APP_CONTEXTS.FORECAST_CREATE: {
      return {
        ...state,
        forecastCreateForm: {
          ...state.forecastCreateForm,
          inventory: {
            ...state.forecastCreateForm.inventory,
            ...inventory
          }
        }
      }
    }
    case APP_CONTEXTS.FORECAST_EDIT: {
      return {
        ...state,
        forecastEditForm: {
          ...state.forecastEditForm,
          inventory: {
            ...state.forecastEditForm.inventory,
            ...inventory
          }
        }
      }
    }
    case APP_CONTEXTS.DSP_DEAL_CREATE: {
      return {
        ...state,
        dspDealCreateForm: {
          ...state.dspDealCreateForm,
          inventory: {
            ...state.dspDealCreateForm.inventory,
            ...inventory
          }
        }
      }
    }
    case APP_CONTEXTS.DSP_DEAL_EDIT: {
      return {
        ...state,
        dspDealEditForm: {
          ...state.dspDealEditForm,
          inventory: {
            ...state.dspDealEditForm.inventory,
            ...inventory
          }
        }
      }
    }
  }
}

function getGeoTargetsFormFromContext(state: IFormState, context: APP_CONTEXTS): IGeoTargetsForm {
  switch (context) {
    case APP_CONTEXTS.CAMPAIGN_CREATE: {
      return state.campaignCreateForm.geoTargetsForm
    }
    case APP_CONTEXTS.CAMPAIGN_EDIT: {
      return state.campaignEditForm.geoTargetsForm
    }
    case APP_CONTEXTS.FORECAST_CREATE: {
      return state.forecastCreateForm.geoTargetsForm
    }
    case APP_CONTEXTS.FORECAST_EDIT: {
      return state.forecastEditForm.geoTargetsForm
    }
    case APP_CONTEXTS.DSP_DEAL_CREATE: {
      return state.dspDealCreateForm.geoTargetsForm
    }
    case APP_CONTEXTS.DSP_DEAL_EDIT: {
      return state.dspDealEditForm.geoTargetsForm
    }
  }
}

function setGeoTargetsFormAndGetNewState(state: IFormState, context: APP_CONTEXTS, form: IGeoTargetsForm): IFormState {
  switch (context) {
    case APP_CONTEXTS.CAMPAIGN_CREATE: {
      return {
        ...state,
        campaignCreateForm: {
          ...state.campaignCreateForm,
          geoTargetsForm: {
            ...state.campaignCreateForm.geoTargetsForm,
            ...form
          }
        }
      }
    }
    case APP_CONTEXTS.CAMPAIGN_EDIT: {
      return {
        ...state,
        campaignEditForm: {
          ...state.campaignEditForm,
          geoTargetsForm: {
            ...state.campaignEditForm.geoTargetsForm,
            ...form
          }
        }
      }
    }
    case APP_CONTEXTS.FORECAST_CREATE: {
      return {
        ...state,
        forecastCreateForm: {
          ...state.forecastCreateForm,
          geoTargetsForm: {
            ...state.forecastCreateForm.geoTargetsForm,
            ...form
          }
        }
      }
    }
    case APP_CONTEXTS.FORECAST_EDIT: {
      return {
        ...state,
        forecastEditForm: {
          ...state.forecastEditForm,
          geoTargetsForm: {
            ...state.forecastEditForm.geoTargetsForm,
            ...form
          }
        }
      }
    }
    case APP_CONTEXTS.DSP_DEAL_CREATE: {
      return {
        ...state,
        dspDealCreateForm: {
          ...state.dspDealCreateForm,
          geoTargetsForm: {
            ...state.dspDealCreateForm.geoTargetsForm,
            ...form
          }
        }
      }
    }
    case APP_CONTEXTS.DSP_DEAL_EDIT: {
      return {
        ...state,
        dspDealEditForm: {
          ...state.dspDealEditForm,
          geoTargetsForm: {
            ...state.dspDealEditForm.geoTargetsForm,
            ...form
          }
        }
      }
    }
  }
}

function getFormFromContext(state: IFormState, context: APP_CONTEXTS): IForm {
  switch (context) {
    case APP_CONTEXTS.CAMPAIGN_CREATE: {
      return state.campaignCreateForm.form
    }
    case APP_CONTEXTS.CAMPAIGN_EDIT: {
      return state.campaignEditForm.form
    }
    case APP_CONTEXTS.FORECAST_CREATE: {
      return state.forecastCreateForm.form
    }
    case APP_CONTEXTS.FORECAST_EDIT: {
      return state.forecastEditForm.form
    }
    case APP_CONTEXTS.DSP_DEAL_CREATE: {
      return state.dspDealCreateForm.form
    }
    case APP_CONTEXTS.DSP_DEAL_EDIT: {
      return state.dspDealEditForm.form
    }
  }
}

function setFormAndGetNewState(state: IFormState, form: IForm): IFormState {
  switch (form.context.appContext) {
    case APP_CONTEXTS.CAMPAIGN_CREATE: {
      return {
        ...state,
        campaignCreateForm: {
          ...state.campaignCreateForm,
          form: {
            ...state.campaignCreateForm.form,
            ...form
          }
        }
      }
    }
    case APP_CONTEXTS.CAMPAIGN_EDIT: {
      return {
        ...state,
        campaignEditForm: {
          ...state.campaignEditForm,
          form: {
            ...state.campaignEditForm.form,
            ...form
          }
        }
      }
    }
    case APP_CONTEXTS.FORECAST_CREATE: {
      return {
        ...state,
        forecastCreateForm: {
          ...state.forecastCreateForm,
          form: {
            ...state.forecastCreateForm.form,
            ...form
          }
        }
      }
    }
    case APP_CONTEXTS.FORECAST_EDIT: {
      return {
        ...state,
        forecastEditForm: {
          ...state.forecastEditForm,
          form: {
            ...state.forecastEditForm.form,
            ...form
          }
        }
      }
    }
    case APP_CONTEXTS.DSP_DEAL_CREATE: {
      return {
        ...state,
        dspDealCreateForm: {
          ...state.dspDealCreateForm,
          form: {
            ...state.dspDealCreateForm.form,
            ...form
          }
        }
      }
    }
    case APP_CONTEXTS.DSP_DEAL_EDIT: {
      return {
        ...state,
        dspDealEditForm: {
          ...state.dspDealEditForm,
          form: {
            ...state.dspDealEditForm.form,
            ...form
          }
        }
      }
    }
  }
}

function getNewFormWithUpdatedLastSavedData(newForm: IForm): IForm {
  return {
    ...newForm,
    context: {
      ...newForm.context,
      lastSavedData: getFormData(newForm)
    }
  }
}
